/**
 * Canonical permission strings: user-service style `resource:action`.
 * Legacy Smart uses kebab `action-resource` (e.g. create-user → user:create).
 */

export function canonicalPermissionName(name: string): string {
  if (!name) return name;
  if (name.includes(':')) return name;
  return legacyKebabToUserService(name) ?? name;
}

function legacyKebabToUserService(legacy: string): string | null {
  if (legacy.includes(':')) return legacy;

  const productSetting =
    /^(basic|intermediate|advanced)-(read|write)-product-settings$/.exec(
      legacy,
    );
  if (productSetting) {
    return `product-setting:${productSetting[1]}-${productSetting[2]}`;
  }

  if (legacy === 'iot-interface') return 'iot-interface:use';
  if (legacy === 'open-ssh') return 'ssh:open';
  if (legacy === 'use-ble-client') return 'ble-client:use';
  if (legacy === 'debug-tool') return 'tool:debug';
  if (legacy === 'read-beta-firmwares') return 'beta-firmware:read';
  if (legacy === 'delete-permissions') return 'permission:delete';
  if (legacy === 'read-reports') return 'report:read';

  switch (legacy) {
    case 'create-files':
      return 'file:create';
    case 'read-files':
      return 'file:read';
    case 'update-files':
      return 'file:update';
    case 'delete-files':
      return 'file:delete';
    case 'create-roles':
      return 'role:create';
    case 'read-roles':
      return 'role:read';
    case 'update-roles':
      return 'role:update';
    case 'delete-roles':
      return 'role:delete';
    case 'read-commission-logs':
      return 'commission-log:read';
    case 'delete-commission-logs':
      return 'commission-log:delete';
    case 'read-diagnostic-files':
      return 'diagnostic-file:read';
    case 'update-diagnostic-files':
      return 'diagnostic-file:update';
    case 'delete-diagnostic-files':
      return 'diagnostic-file:delete';
    case 'create-alerts':
      return 'alert:create';
    case 'read-alerts':
      return 'alert:read';
    case 'update-alerts':
      return 'alert:update';
    case 'delete-alerts':
      return 'alert:delete';
    case 'create-hardware-components':
      return 'hardware-component:create';
    case 'read-hardware-components':
      return 'hardware-component:read';
    case 'update-hardware-components':
      return 'hardware-component:update';
    case 'delete-hardware-components':
      return 'hardware-component:delete';
    default:
      break;
  }

  const m = /^(create|read|update|delete|disguise)-(.+)$/.exec(legacy);
  if (!m) return null;
  const [, action, resource] = m;
  return `${resource}:${action}`;
}

/** Prefer canonical `resource:action` rows when the catalog has legacy + modern duplicates. */
export function dedupePermissionCatalog<
  T extends { id: string; name: string },
>(catalog: T[]): T[] {
  const byCanonical = new Map<string, T>();
  for (const item of catalog) {
    const key = canonicalPermissionName(item.name);
    const existing = byCanonical.get(key);
    if (!existing) {
      byCanonical.set(key, item);
      continue;
    }
    if (item.name.includes(':') && !existing.name.includes(':')) {
      byCanonical.set(key, item);
    }
  }
  return [...byCanonical.values()];
}

export function collapseDirectPermissionIds(
  ids: Iterable<string>,
  catalog: Array<{ id: string; name: string }>,
): Set<string> {
  const catalogById = new Map(catalog.map((c) => [c.id, c]));
  const byCanonical = new Map<string, string>();

  for (const id of ids) {
    const perm = catalogById.get(id);
    if (!perm) continue;
    const key = canonicalPermissionName(perm.name);
    const existingId = byCanonical.get(key);
    if (!existingId) {
      byCanonical.set(key, id);
      continue;
    }
    const existing = catalogById.get(existingId)!;
    if (perm.name.includes(':') && !existing.name.includes(':')) {
      byCanonical.set(key, id);
    }
  }

  return new Set(byCanonical.values());
}
